import { Star } from 'lucide-react';
import { PaymentMethodIcon } from './CardBrandIcon';
import { describeSavedCard, type PaymentMethodType } from '../utils/paymentMethodDisplay';

/**
 * Compact payment-method chip for dense lists (e.g. the admin Parents table). Same icon,
 * naming and Default marker as the parent Account page's `PaymentMethodRow`, sized to fit a
 * table cell.
 */
function PaymentMethodChip({ type, brand, name, isDefault }: {
  type: PaymentMethodType;
  brand?: string | null;
  name: string;
  isDefault?: boolean;
}) {
  return (
    <div
      className={`flex w-56 items-center gap-2 rounded-lg border px-2.5 py-1.5 shadow-sm ${
        isDefault ? 'border-blue-200 bg-blue-50/60' : 'border-gray-200 bg-white'
      }`}
    >
      <div className="shrink-0">
        <PaymentMethodIcon type={type} brand={brand} size={18} />
      </div>
      <span className="min-w-0 flex-1 truncate text-xs font-medium text-gray-900" title={name}>
        {name}
      </span>
      {isDefault && (
        <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-blue-700">
          <Star className="h-2.5 w-2.5 fill-blue-600 text-blue-600" />
          Default
        </span>
      )}
    </div>
  );
}

interface CardPaymentBadgeProps {
  brand: string | null;
  last4: string | null;
  isDefault?: boolean;
}

export function CardPaymentBadge({ brand, last4, isDefault }: CardPaymentBadgeProps) {
  const { name } = describeSavedCard({ brand, last4 });
  return <PaymentMethodChip type="card" brand={brand} name={name} isDefault={isDefault} />;
}

interface BankPaymentBadgeProps {
  bank_name: string | null;
  last4: string | null;
  isDefault?: boolean;
}

export function BankPaymentBadge({ bank_name, last4, isDefault }: BankPaymentBadgeProps) {
  return (
    <PaymentMethodChip
      type="bank_account"
      name={`${bank_name ?? 'Bank'} •••• ${last4 ?? '••••'}`}
      isDefault={isDefault}
    />
  );
}
